import type { ServiceRecord } from "../index";

export const service: ServiceRecord = {
  slug: "agricultural-work-management",
  kind: "domain-vertical",
  title: "Agricultural Work & Crew Management Systems",
  shortTitle: "Work Management",
  status: "draft",
  tier: "we-do",
  seoTitle: "Agricultural Work & Crew Management Systems | GA",
  seoDescription:
    "Field task scheduling, crew dispatching, equipment allocation, and operational handoffs designed for commercial farm operations.",
  summary:
    "Operational task management, crew dispatch, equipment tracking, and shift handoffs that turn seasonal farm plans into accountable daily field work.",
  image: "/images/blog/operational-task-management-hero.webp",
  relatedCapabilitySlugs: [],
  relatedIndustrySlugs: ["agriculture"],
  relatedPostSlugs: [
    "operational-task-management",
    "dashboard-design",
    "succession-calendars-living-inventories",
  ],
  engineering: [
    "Explicit task state machines (scheduled, ready, active, weather-delayed, completed, verified)",
    "Crew-level task dispatching and daily work-order assignment tied to specific fields, blocks, and beds",
    "Equipment, implement, and tool reservation tracking preventing operational scheduling conflicts",
    "Time-tracking and labor-hour allocation across specific crops, plantings, and management activities",
    "Mobile-optimized operator task views with offline caching for field workers in low-connectivity zones",
    "Supervisory review, sign-off queues, and work verification audits ensuring operational standards",
  ],
  body: [
    {
      type: "heading",
      level: 2,
      text: "Field Work Requires Explicit Operational State",
    },
    {
      type: "paragraph",
      text: "A generic calendar or to-do list cannot manage the operational reality of agricultural labor. Field tasks depend on soil moisture, weather windows, equipment availability, and crew qualifications. When work orders exist only on whiteboards or verbal shift handoffs, critical cultivation passes are missed, beds are weeded too late, and equipment conflicts stop work in the middle of a shift.",
    },
    {
      type: "heading",
      level: 2,
      text: "Connecting Tasks Directly to the Land and Crop",
    },
    {
      type: "paragraph",
      text: "Operational agricultural work systems bind tasks directly to specific beds, crop plantings, and equipment assets. This creates an accountable operational log: who prepped the bed, what implements were used, when the crop was cultivated, and how many labor hours were invested per row foot, providing management with accurate cost-of-production data across every harvest.",
    },
  ],
  phases: {
    study: {
      focus:
        "Audit farm labor workflows, crew structures, task communication routines, and equipment utilization bottlenecks.",
      produces: [
        "Farm labor and operational workflow inventory",
        "Role hierarchy, crew assignment, and supervision structure mapping",
        "Equipment and implement dependency matrix",
        "Task communication friction and labor-tracking error analysis",
      ],
      fit: {
        good: [
          "Commercial agricultural operations employing field crews, managers, and specialized equipment operators",
          "Farms looking to replace paper clipboards, verbal handoffs, and whiteboard lists with accountable digital task queues",
        ],
        poor: [
          "Solo micro-operations where complex multi-worker crew dispatching and role hierarchies are unneeded",
          "Organizations seeking standard enterprise office project management tools detached from physical land and equipment",
        ],
      },
    },
    "initial-design": {
      focus:
        "Architect the task state machine, role-based access models, crew dispatch logic, and mobile interaction flows.",
      produces: [
        "Agricultural task state machine schema (scheduled, ready, in-progress, weather-hold, complete, verified)",
        "Crew and equipment allocation models with conflict-detection rules",
        "Labor-hour logging and crop-attribution data architecture",
        "Mobile task view wireframes optimized for fast field interaction",
      ],
    },
    "detailed-design": {
      focus:
        "Specify offline caching mechanisms, priority queue algorithms, supervisory approval workflows, and reporting APIs.",
      produces: [
        "Offline synchronization protocols for mobile field worker devices",
        "Dynamic task reprioritization rules based on weather and soil status",
        "Supervisory sign-off and quality-verification workflow specifications",
        "Labor cost attribution and enterprise payroll export interfaces",
      ],
    },
    implementation: {
      focus:
        "Build and deploy the work management system, equip field crews with mobile task views, and establish supervisory review workflows.",
      produces: [
        "Production agricultural work and crew management application",
        "Configured crew rosters, equipment registries, and recurring task templates",
        "Active field dispatch boards and supervisory approval queues",
        "Field crew training documentation and standard operating procedures",
      ],
    },
  },
};
